/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {
  saveJob,
  unFollowJob,
  unFollowRecruiter,
} from '@jobseeker/common-core/reducers/job/thunkServices/jobfeed';
import JobFeedV2 from '@jobseeker/feature-job/components/JobFeed/V2';
import surveyService from '@jobseeker/common-core/services/survey.service';
import profileService from '@jobseeker/common-core/services/profile.service';
import {useEffect, useMemo, useState} from 'react';
import {SurveyQuestionType} from '@jobseeker/feature-ads/components/Survey/types';
import {Survey as SurveyType} from '@jobseeker/common-core/services/types';
import {
  getAllParamsFromUrl,
  handleTracking,
  TrackEventUBA,
} from '@jobseeker/common-core/utils';
import dayjs from 'dayjs';
import JobsJsonLd from '@jobseeker/feature-job/components/JobItemListingSchema';

import type {AppState} from '@jobseeker/common-core/types';
import type {AdElement} from '@jobseeker/feature-ads/components/AdsBanner/types';
import {JOBAPPLIED, JOBDETAIL, JOBFEED} from '@jobseeker/common-core/constants';
import type {SalaryNudgeVariant} from '../components/SalaryNudge';

type Fn = () => any;
type JobFeedsListViewProps = {
  routeToJobDetail: (jobSlug: string, jobId: string | number) => void;
  onLoadMore: Fn;
  jobInfoKeyElements: any;
  feedOnly: boolean;
  title: string;
  showThreeDotMenu?: boolean;
  onFilter?: (filterData: any) => void;
  onClearFilter?: () => void;
  onInfiniteScroll?: () => void;
  learnGrow?: AdElement;
  variant?: string;
  injectAsChild?: boolean;
  onFilterChange?: (filterData: object) => void;
  onJobSave?: (data: object) => void;
  showApplied?: boolean;
  catId?: string | number;
  checkJobs?: Map<string, string | undefined>;
  cityJobs?: any;
  preFilters?: {
    page: number;
  } & Record<string, any>;
  extractedParams?: any;
  showIndustry?: boolean;
  showStickyFilter?: boolean;
  showApplyButton?: boolean;
  showCourses?: boolean;
  hideSidebar?: boolean;
  calledType?: string;
  salaryNudgeVariant?: SalaryNudgeVariant;
};
const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

const JobFeedsListV2 = ({
  routeToJobDetail,
  onLoadMore,
  jobInfoKeyElements,
  feedOnly,
  title,
  onFilter,
  onInfiniteScroll,
  onClearFilter,
  variant,
  onFilterChange,
  extractedParams,
  preFilters,
  calledType,
  salaryNudgeVariant: salaryNudgeVariantProp,
  ...props
}: JobFeedsListViewProps) => {
  const dispatch = useDispatch<any>();

  const {isLoggedIn, userId} = useSelector((state: AppState) => state.auth);
  const tags = useSelector((state: AppState) => state.user?.profile?.tags);
  const profileLoading = useSelector(
    (state: AppState) => state.user?.profileLoading
  );

  const jobFeedStore = useSelector((state: AppState) => state.job);

  const page = useSelector((state: AppState) => state.app.page, shallowEqual);
  const hideFilter =
    profileLoading === false && !tags?.length && page === 'jobfeed'
      ? true
      : feedOnly;

  const allparamsfromurl = getAllParamsFromUrl() || {};

  const onJobSave = (jobData: any, unsave: boolean) => {
    if (isLoggedIn) {
      dispatch(
        saveJob({
          ...jobData,
          jobId: jobData.id,
          unsave,
          userId: userId,
        })
      );
    }
    props.onJobSave &&
      props.onJobSave({
        ...jobData,
        jobId: jobData.id,
        unsave,
        userId: userId,
      });
  };

  const onJobActionClick = (jobId: string, action: string) => {
    if (action == 'JOB') {
      dispatch(
        unFollowJob({
          undo: false,
          jobId: jobId,
          userId: userId,
        })
      );
    } else {
      dispatch(
        unFollowRecruiter({
          undo: false,
          recruiterId: 'NO_REC_ID',
          userId: userId,
        })
      );
    }
  };

  const onUndoActionClick = (jobId: string, action: string) => {
    if (action == 'JOB') {
      dispatch(
        unFollowJob({
          undo: true,
          jobId: jobId,
          userId: userId,
        })
      );
    } else {
      dispatch(
        unFollowRecruiter({
          undo: true,
          recruiterId: 'NO_REC_ID',
          userId: userId,
        })
      );
    }
  };

  const [showSurvey, setShowSurvey] = useState(false);
  const [surveyQuestions, setSurveyQuestions] = useState<SurveyQuestionType[]>(
    []
  );
  const [currQuestion, setCurrQuestion] = useState<SurveyQuestionType>(
    surveyQuestions[0]
  );

  const [showMarketingSurvey, setShowMarketingSurvey] = useState(false);
  const [marketingSurveyQuestions, setMarketingSurveyQuestions] = useState<
    SurveyQuestionType[]
  >([]);

  const currentUserSalary = useSelector(
    (state: AppState) => state.user?.profile?.currentSalary ?? 0
  );

  const profileExperienceYear = useSelector(
    (state: AppState) => state.user?.profile?.experienceYear ?? 0
  );

  const currentSalaryUpdatedInLast30Days = useSelector(
    (state: AppState) =>
      state.user?.profile?.currentSalaryUpdatedInLast30Days ?? false
  );

  const profileSchema = useSelector(
    (state: AppState) =>
      (state.user?.profile?.schema ?? {}) as Record<string, string>
  );

  const currentConfidential = useSelector(
    (state: AppState) => state.user?.profile?.confidential ?? 0
  );

  // ── Salary nudge – configurable constants ─────────────────────────────────
  const NUDGE_MAX_CTC_RUPEES = 500_000; // 5 LPA
  const NUDGE_MIN_EXP_YEARS = 10;
  const NUDGE_FREQUENCY_DAYS = 3;
  const NUDGE_LIFETIME_CAP = 3;
  const NUDGE_SESSION_KEY = 'salary_nudge_session';
  const NUDGE_COUNT_KEY = 'salary_nudge_count';
  const NUDGE_LAST_SHOWN_KEY = 'salary_nudge_last_shown';

  const canShowNudge = (): boolean => {
    try {
      if (sessionStorage.getItem(NUDGE_SESSION_KEY)) return false;
      const count = parseInt(localStorage.getItem(NUDGE_COUNT_KEY) ?? '0', 10);
      if (count >= NUDGE_LIFETIME_CAP) return false;
      const lastShown = parseInt(
        localStorage.getItem(NUDGE_LAST_SHOWN_KEY) ?? '0',
        10
      );
      if (lastShown) {
        const daysSince = (Date.now() - lastShown) / 86_400_000;
        if (daysSince < NUDGE_FREQUENCY_DAYS) return false;
      }
    } catch {
      return false;
    }
    return true;
  };

  const recordNudgeImpression = (): void => {
    try {
      sessionStorage.setItem(NUDGE_SESSION_KEY, '1');
      const count = parseInt(localStorage.getItem(NUDGE_COUNT_KEY) ?? '0', 10);
      localStorage.setItem(NUDGE_COUNT_KEY, String(count + 1));
      localStorage.setItem(NUDGE_LAST_SHOWN_KEY, String(Date.now()));
    } catch {}
  };

  const exhaustNudgeCohort = (): void => {
    try {
      localStorage.setItem(NUDGE_COUNT_KEY, String(NUDGE_LIFETIME_CAP));
    } catch {}
  };

  const [showSalaryNudge, setShowSalaryNudge] = useState(false);
  const [nudgeCurrentSalary, setNudgeCurrentSalary] = useState(0);

  useEffect(() => {
    if (!salaryNudgeVariantProp) return;
    if (!canShowNudge()) return;

    const eligible =
      profileExperienceYear >= NUDGE_MIN_EXP_YEARS &&
      currentUserSalary > 0 &&
      currentUserSalary <= NUDGE_MAX_CTC_RUPEES &&
      !currentSalaryUpdatedInLast30Days;

    if (eligible) {
      setNudgeCurrentSalary(currentUserSalary);
      setShowSalaryNudge(true);
      recordNudgeImpression();
    }
  }, [
    salaryNudgeVariantProp,
    profileExperienceYear,
    currentUserSalary,
    currentSalaryUpdatedInLast30Days,
  ]);

  const salaryNudgeVariant: SalaryNudgeVariant | undefined = showSalaryNudge
    ? salaryNudgeVariantProp ?? 'A'
    : undefined;

  const onSalaryConfirm = () => {
    setShowSalaryNudge(false);
  };

  const buildSchemaFromUrl = async (): Promise<Record<string, string>> => {
    const schemaUrl = process.env.NEXT_PUBLIC_PROFILE_SCHEMA;
    if (!schemaUrl) return {};
    try {
      const res = await fetch(schemaUrl);
      const raw: Record<
        string,
        Record<string, {field: string}>
      > = await res.json();
      const built: Record<string, string> = {};
      Object.keys(raw).forEach(section => {
        Object.keys(raw[section]).forEach(key => {
          built[`${section}.${raw[section][key].field}`] = key;
        });
      });
      return built;
    } catch {
      return {};
    }
  };

  const onSalaryUpdate = async (
    salaryInRupees: number,
    confidential: number
  ) => {
    let schema = profileSchema;
    if (!schema['pd.currentSalary']) {
      schema = await buildSchemaFromUrl();
    }
    const salarySchemaKey = schema['pd.currentSalary'] ?? '';
    const confidentialSchemaKey = schema['pd.confidential'] ?? '';
    const infoString = salarySchemaKey + confidentialSchemaKey;
    const requiredString = infoString;
    await profileService.updateProfile(
      {
        pd: {
          infoString,
          requiredString,
          currentSalary: salaryInRupees,
          confidential: confidential,
        },
      },
      '',
      1,
      'update_current_salary_nudge'
    );
    dispatch({
      type: 'User/set_user_profile',
      payload: {currentSalary: salaryInRupees},
    });
    exhaustNudgeCohort();
    setShowSalaryNudge(false);
  };

  const [marketingCurrQuestion, setMarketingCurrQuestion] =
    useState<SurveyQuestionType>(marketingSurveyQuestions[0]);
  const [marketingSurveyPosition, setMarketingSurveyPosition] = useState('');
  const fetchSurveys = async () => {
    const surveys: SurveyType[] = await surveyService.getSurvey('JF');
    if (surveys?.length) {
      const jobFeedSurvey = surveys.find(
        survey => survey?.type === 'RelevantJob'
      );
      if (jobFeedSurvey) {
        setShowSurvey(true);
        handleTracking('jsViewRelevantJobSurvey', {
          event_category: 'Job_recommedation_survey',
          UserId: sessionStorage.getItem('userId') || 'NA',
          Device: sessionStorage.getItem('device') || 'desktop',
          Device_Type: sessionStorage.getItem('deviceType') || 'web',
          Status: sessionStorage.getItem('status') || 'LoggedOut',
          Origin: page,
          Date: dayjs().format('DD/MM/YYYY'),
        });
        setSurveyQuestions(jobFeedSurvey?.questions);
        setCurrQuestion(jobFeedSurvey?.questions[0]);
      }
    }
  };

  const fetchMarketingSurveys = async () => {
    const surveys: SurveyType[] = await surveyService.getSurvey('JOBFEED');
    if (surveys?.length) {
      const jobFeedSurvey = surveys.find(
        survey => survey?.type === 'Marketing' || survey?.type === 'MapView'
      );
      if (jobFeedSurvey) {
        setShowMarketingSurvey(true);
        // Fire UBA surveyView event when survey is displayed
        if (jobFeedSurvey.type === 'Marketing') {
          TrackEventUBA('surveyView', 'view', {
            pageName: page === 'jobfeed' ? 'jobfeed' : 'similarJobs',
            sessionUsername: sessionStorage.getItem('userId') || '',
            surveyName: 'Marketing PR Survey MBA Cohort',
            surveyId: jobFeedSurvey?.id,
          });
        } else if (jobFeedSurvey.type === 'MapView') {
          TrackEventUBA('surveyView', 'view', {
            pageName: page === 'jobfeed' ? 'jobfeed' : 'similarJobs',
            sessionUsername: sessionStorage.getItem('userId') || '',
            surveyName: 'Map View Survey',
            surveyId: jobFeedSurvey?.id,
          });
        }
        setMarketingSurveyQuestions(jobFeedSurvey?.questions);
        setMarketingCurrQuestion(jobFeedSurvey?.questions[0]);
        if (allparamsfromurl.ref === 'survey_marketing_pr_mbas') {
          setMarketingSurveyPosition('0'); // Show at top
        } else {
          setMarketingSurveyPosition(jobFeedSurvey?.position);
        }
      }
    }
  };

  const marketingSurveyPositions = useMemo(() => {
    if (!marketingSurveyPosition || marketingSurveyPosition?.trim() === '')
      return [];
    return marketingSurveyPosition
      .split(',')
      .map(pos => {
        const position = parseInt(pos.trim(), 10);
        return position - 1;
      })
      .filter(pos => !isNaN(pos));
  }, [marketingSurveyPosition]);

  useEffect(() => {
    if (page === 'jobfeed') {
      if (isAppIimjobs) {
        fetchSurveys();
      } else {
        fetchMarketingSurveys();
      }
    }
    // if (page === 'jobfeed') {
    //   fetchSurveys();
    // }
    // if ([JOBFEED, JOBAPPLIED, JOBDETAIL].includes(page) && isAppIimjobs) {
    //   fetchMarketingSurveys();
    // }
  }, [page]);

  // const JobFeedComponent =
  //   process.env.NEXT_PUBLIC_APP_NAME === 'hirist' ? JobFeedHirist : JobFeedV2;

  // console.log(
  //   process.env.NEXT_PUBLIC_APP_NAME,
  //   'JobFeedHirist',
  //   JobFeedComponent
  // );

  return (
    <>
      {!props?.injectAsChild && (
        <JobsJsonLd
          name={`${title}`}
          jobs={[
            ...jobFeedStore?.jobfeed,
            ...(page === 'location_based' &&
            props?.cityJobs?.jobs?.length > 0 &&
            jobFeedStore?.jobfeed?.length < 20 &&
            !isAppIimjobs
              ? props?.cityJobs?.jobs
              : []),
          ]}
        />
      )}
      <JobFeedV2
        onLoadMore={onLoadMore}
        onUndoActionClick={onUndoActionClick}
        onJobActionClick={onJobActionClick}
        onFilter={onFilter}
        onClearFilter={onClearFilter}
        feedOnly={feedOnly}
        title={title}
        variant={isLoggedIn ? 'userFeed' : ''}
        jobFeedStore={jobFeedStore}
        jobInfoKeyElements={jobInfoKeyElements}
        routeToJobDetail={routeToJobDetail}
        showSurvey={showSurvey}
        surveyQuestions={surveyQuestions}
        setShowSurvey={setShowSurvey}
        currQuestion={currQuestion}
        setCurrentQuestion={setCurrQuestion}
        showMarketingSurvey={showMarketingSurvey}
        marketingSurveyQuestions={marketingSurveyQuestions}
        setShowMarketingSurvey={setShowMarketingSurvey}
        marketingCurrQuestion={marketingCurrQuestion}
        setMarketingCurrQuestion={setMarketingCurrQuestion}
        marketingSurveyPositions={marketingSurveyPositions}
        page={page}
        onInfiniteScroll={onInfiniteScroll}
        onFilterChange={onFilterChange}
        {...props}
        onJobSave={onJobSave}
        showFilter={!hideFilter}
        showApplied={props.showApplied || false}
        preFilters={preFilters}
        extractedParams={extractedParams}
        calledType={calledType}
        salaryNudgeVariant={salaryNudgeVariant}
        currentUserSalary={nudgeCurrentSalary || currentUserSalary}
        currentUserConfidential={currentConfidential}
        onSalaryConfirm={onSalaryConfirm}
        onSalaryUpdate={onSalaryUpdate}
      />
    </>
  );
};

export default JobFeedsListV2;
